import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expendituresService, expenditureCategoriesService } from '../services/apiServices';
import { Expenditure, ExpenditureCategory, ExpenditureGroup, PaginatedResponse } from '../types';

// Get all expenditures (grouped by procurement)
export const useExpenditures = (params?: any) => {
  return useQuery<PaginatedResponse<ExpenditureGroup>>({
    queryKey: ['expenditures', params],
    queryFn: () => expendituresService.getAll(params),
  });
};

// Get single expenditure
export const useExpenditure = (id: number) => {
  return useQuery<Expenditure>({
    queryKey: ['expenditure', id],
    queryFn: () => expendituresService.getById(id),
    enabled: !!id,
  });
};

// Get expenditures stats
export const useExpendituresStats = (params?: any) => {
  return useQuery({
    queryKey: ['expendituresStats', params],
    queryFn: () => expendituresService.getStats(params),
  });
};

// Get pending expenditures
export const usePendingExpenditures = () => {
  return useQuery<Expenditure[]>({
    queryKey: ['pendingExpenditures'],
    queryFn: () => expendituresService.getPending(),
  });
};

// Get expenditure trends
export const useExpenditureTrends = () => {
  return useQuery({
    queryKey: ['expenditureTrends'],
    queryFn: () => expendituresService.getTrends(),
  });
};

// Get all expenditures (flat list, not grouped)
export const useAllExpendituresFlat = (params?: any) => {
  return useQuery<PaginatedResponse<Expenditure>>({
    queryKey: ['expendituresFlat', params],
    queryFn: () => expendituresService.getAllFlat(params),
  });
};

// Create expenditure mutation
export const useCreateExpenditure = (params?: any) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => expendituresService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['expendituresStats', params] });
      queryClient.invalidateQueries({ queryKey: ['pendingExpenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview', params] });
      queryClient.invalidateQueries({ queryKey: ['expendituresFlat', params] });
    },
  });
};

// Update expenditure mutation
export const useUpdateExpenditure = (params?: any) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => expendituresService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['expenditure', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['expendituresStats', params] });
      queryClient.invalidateQueries({ queryKey: ['pendingExpenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview', params] });
      queryClient.invalidateQueries({ queryKey: ['expendituresFlat'] });
    },
  });
};

// Delete expenditure mutation
export const useDeleteExpenditure = (params?: any) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => expendituresService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['expendituresStats', params] });
      queryClient.invalidateQueries({ queryKey: ['pendingExpenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview', params] });
      queryClient.invalidateQueries({ queryKey: ['expendituresFlat', params] });
    },
  });
};

// Make payment mutation
export const useMakePayment = (params?: any) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      expendituresService.makePayment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenditures', params] });
      queryClient.invalidateQueries({ queryKey: ['expenditure', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['expendituresStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingExpenditures'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview'] });
      queryClient.invalidateQueries({ queryKey: ['expendituresFlat', params] });
    },
  });
};

// ============== Expenditure Categories ==============

// Get all expenditure categories
export const useExpenditureCategories = () => {
  return useQuery<ExpenditureCategory[]>({
    queryKey: ['expenditure-categories'],
    queryFn: () => expenditureCategoriesService.getAll(),
  });
};

// Get single expenditure category
export const useExpenditureCategory = (id: number) => {
  return useQuery<ExpenditureCategory>({
    queryKey: ['expenditureCategory', id],
    queryFn: () => expenditureCategoriesService.getById(id),
    enabled: !!id,
  });
};

// Create expenditure category mutation
export const useCreateExpenditureCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => expenditureCategoriesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenditure-categories'] });
    },
  });
};

// Update expenditure category mutation
export const useUpdateExpenditureCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      expenditureCategoriesService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expenditure-categories'] });
      queryClient.invalidateQueries({ queryKey: ['expenditureCategory', variables.id] });
    },
  });
};

// Delete expenditure category mutation
export const useDeleteExpenditureCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => expenditureCategoriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenditure-categories'] });
    },
  });
};
